import React, { useEffect, useState } from 'react'
import { Modal } from 'antd'
import styles from './Users.module.less'
import { useAppDispatch, useAppSelector } from '~/Store'
import { UsersActions } from '~/Store/AdminUser'
import { getListUsers } from '~/Store/AdminUser/selectors'
import { UserResponse } from '~/Services/AdminUsers'
import { getListRoles, getRolesToUserId } from '~/Store/Role/selectors'
import { RolesActions } from '~/Store/Role'
import { CaretDownOutlined, DeleteOutlined } from '@ant-design/icons'

export const AdminUsers = () => {
  const dispatch = useAppDispatch()

  const usersResponse = useAppSelector(getListUsers)
  const users = usersResponse?.results ?? []

  const rolesUserID = useAppSelector(getRolesToUserId)

  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const roleAdminData = useAppSelector(getListRoles)
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null)

  useEffect(() => {
    dispatch(RolesActions.getRolesRequest())
    dispatch(UsersActions.getUsersRequest())
  }, [dispatch])

  const getInvitationStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'orange'
      case 'Accepted':
        return 'green'
      case 'Rejected':
        return 'red'
      default:
        return 'gray'
    }
  }

  const showUserModal = (user: UserResponse) => {
    setSelectedUser(user)
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedUser(null)
    setSelectedRoles([])
  }

  const handleCheckboxChange = (rolename: string, checked: boolean) => {
    setSelectedRoles(prev => {
      if (checked) {
        if (!prev.includes(rolename)) {
          return [...prev, rolename]
        }
      } else {
        return prev.filter(name => name !== rolename)
      }
      return prev
    })
  }

  const handleOk = () => {
    if (selectedUser && selectedRoles.length > 0) {
      const roleNameString = selectedRoles.join(',')
      dispatch(
        RolesActions.updateUserRoles({
          userId: selectedUser.id,
          roleName: roleNameString,
        }),
      )
    }
    handleCloseModal()
  }

  const handleCaretClick = (e: React.MouseEvent, user: UserResponse) => {
    e.stopPropagation()
    dispatch(RolesActions.getUserRolesRequest(user.id))
    setExpandedUserId(prev => (prev === user.id ? null : user.id))
  }

  return (
    <div className="table-container">
      {users.map(user => (
        <React.Fragment key={user.id}>
          <div
            className={`${styles.tableRow} ${
              expandedUserId === user.id ? styles.expanded : ''
            }`}
            onClick={() => showUserModal(user)}
          >
            <div className={styles.tableCell}>
              {`${user.lastname ?? ''} ${user.firstname ?? ''}`}
            </div>
            <div className={styles.tableCell}>{user.email}</div>
            <div
              className={styles.tableCell}
              style={{ color: getInvitationStatusColor(user.invitationStatus) }}
            >
              {user.invitationStatus || 'Không rõ'}
            </div>
            <div
              onClick={e => handleCaretClick(e, user)}
              style={{ padding: '0 8px' }}
            >
              <CaretDownOutlined style={{ fontSize: 16 }} />
            </div>
          </div>

          {/* Roles dưới row */}
          {expandedUserId === user.id && (
            <div className={styles.rolesSection}>
              <strong>Vai trò:</strong>
              <ul>
                {rolesUserID?.length > 0 ? (
                  rolesUserID.map((role, idx) => (
                    <li key={idx} className={styles.roleItem}>
                      <span>{role}</span>
                      <DeleteOutlined
                        className={styles.deleteIcon}
                        onClick={() => {
                          Modal.confirm({
                            title: 'Xác nhận xóa',
                            content: `Bạn có chắc chắn muốn xóa vai trò "${role}" khỏi người dùng này không?`,
                            okText: 'Xóa',
                            okType: 'danger',
                            cancelText: 'Hủy',
                            onOk() {
                              dispatch(
                                RolesActions.deleteUserRolesRequest({
                                  userId: user.id,
                                  roleName: [role],
                                }),
                              )
                            },
                          })
                        }}
                      />
                    </li>
                  ))
                ) : (
                  <li className={styles.noRole}>Không có</li>
                )}
              </ul>
            </div>
          )}
        </React.Fragment>
      ))}

      <Modal
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleOk}
        title="Danh sách vai trò"
        centered
        width={600}
      >
        <div className={styles.roleModalWrapper}>
          <strong>Chọn vai trò:</strong>
          <div className={styles.checkboxGroup}>
            {roleAdminData?.map(role => (
              <div key={role.name} className={styles.checkboxItem}>
                <label>
                  <input
                    type="checkbox"
                    value={role.name}
                    checked={selectedRoles.includes(role.name)}
                    onChange={e =>
                      handleCheckboxChange(role.name, e.target.checked)
                    }
                  />
                  {role.name}
                </label>
              </div>
            ))}
          </div>
          <p className={styles.roleNote}>* Chỉ được chọn 1 vai trò</p>
        </div>
      </Modal>
    </div>
  )
}
